import React, { useEffect, useState, useRef } from "react";
import "./UserProfile.css";
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { main_server } from "../../constants";
import dayjs from "dayjs";
import { styled } from '@mui/material/styles';
import { Tabs, Tab, Box, Typography, CircularProgress, Tooltip, Paper, Divider, Grid } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

// Custom styled tabs for the game modes
const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: '#6ED0E8',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 'bold',
  fontSize: '0.9rem',
  color: '#888',
  '&.Mui-selected': {
    color: '#6ED0E8',
  },
}));

// Mock data for development - will be replaced with API calls
const mockUserData = {
  name: "",
  username: "",
  joinDate: "",
  totalGames: 0,
  wins: 0,
  losses: 0,
  draws: 0,
  winRate: 0,
  avatar: null, // Will use initials if null
  currentAccuracy: 0, // Current overall accuracy percentage
  elo: 1000, // Current ELO rating
  percentileRank: 0, // Percentage of players they are higher rated than
};


// Mock activity data
const mockActivityData = [
  {
    id: 1,
    type: 'elo_milestone',
    title: 'Reached 2000 ELO in Blitz',
    time: '2 days ago',
    icon: '🏆',
  },
  {
    id: 2,
    type: 'win_streak',
    title: 'Won 5 games in a row',
    time: '3 days ago',
    icon: '🔥',
  },
  {
    id: 3,
    type: 'tournament',
    title: 'Finished 3rd in Weekend Blitz Tournament',
    time: '1 week ago',
    icon: '🎖️',
  },
  {
    id: 4,
    type: 'elo_change',
    title: 'Gained 25 ELO points in Rapid',
    time: '1 week ago',
    icon: '📈',
  },
  {
    id: 5,
    type: 'game_milestone',
    title: 'Played 1000th game',
    time: '2 weeks ago',
    icon: '🎮',
  },
];

// Mock games data
const mockGamesData = [
  {
    id: 1,
    opponent: '',
    result: 'win',
    gameType: 'Blitz',
    date: '',
    time: '',
    playerRating: 1000,
    opponentRating: 1000,
    ratingChange: 0,
  }
];

// Mock accuracy data
const mockAccuracyData = {
  // Overall accuracy over time
  historyData: [
    { date: '2024-01-01', accuracy: 85.3 },
    { date: '2024-02-01', accuracy: 86.1 },
    { date: '2024-03-01', accuracy: 87.4 },
    { date: '2024-04-01', accuracy: 88.9 },
    { date: '2024-05-01', accuracy: 90.2 },
    { date: '2024-06-01', accuracy: 91.5 },
    { date: '2024-07-01', accuracy: 92.7 },
  ],
  // Accuracy by game stage
  gameStages: {
    opening: 94.2,
    middlegame: 91.8,
    endgame: 89.5,
  },
  // Accuracy by time control
  timeControls: {
    bullet: 88.3,
    blitz: 91.5,
    rapid: 93.7,
    classical: 95.2,
  },
  // Accuracy by piece movement
  pieceAccuracy: {
    pawn: 93.1,
    knight: 90.4,
    bishop: 92.7,
    rook: 94.3,
    queen: 91.8,
    king: 89.5,
  },
  // Accuracy in critical positions
  criticalPositions: {
    tactical: 88.6,
    positional: 91.2,
    advantage: 94.5,
    equal: 92.8,
    disadvantage: 85.3,
  },
  // Recent games accuracy
  recentGames: [
    { id: 1, opponent: 'GrandMaster123', accuracy: 94.7, opponentAccuracy: 92.1, result: 'win' },
    { id: 2, opponent: 'ChessWizard', accuracy: 91.2, opponentAccuracy: 93.5, result: 'loss' },
    { id: 3, opponent: 'KnightRider', accuracy: 93.8, opponentAccuracy: 90.2, result: 'win' },
    { id: 4, opponent: 'QueenGambit', accuracy: 92.5, opponentAccuracy: 92.7, result: 'draw' },
    { id: 5, opponent: 'PawnStar', accuracy: 95.1, opponentAccuracy: 89.8, result: 'win' },
  ],
  // Accuracy improvement suggestions
  improvementAreas: [
    { area: 'Pawn structure evaluation', currentAccuracy: 87.3, potentialGain: 3.2 },
    { area: 'Knight outposts', currentAccuracy: 85.6, potentialGain: 4.1 },
    { area: 'Rook endgames', currentAccuracy: 83.9, potentialGain: 5.3 },
    { area: 'Queen vs. Rook tactics', currentAccuracy: 86.2, potentialGain: 3.8 },
    { area: 'King safety', currentAccuracy: 88.4, potentialGain: 2.9 },
  ],
};

// Mock theory data
const mockTheoryData = {
  // Average moves before leaving theory
  theoryMoves: {
    asWhite: 12.3,
    asBlack: 10.8,
  },
  // Opening repertoire
  openings: {
    white: [
      { name: "Queen's Gambit", games: 145, wins: 68.3, avgMoves: 14.2 },
      { name: "Ruy Lopez", games: 98, wins: 72.1, avgMoves: 16.5 },
      { name: "London System", games: 87, wins: 65.7, avgMoves: 12.8 },
      { name: "Italian Game", games: 76, wins: 59.2, avgMoves: 13.7 },
      { name: "Vienna Game", games: 42, wins: 71.4, avgMoves: 11.3 },
    ],
    black: [
      { name: "Sicilian Defense", games: 132, wins: 63.6, avgMoves: 15.3 },
      { name: "French Defense", games: 104, wins: 58.7, avgMoves: 14.1 },
      { name: "Caro-Kann", games: 89, wins: 61.8, avgMoves: 12.9 },
      { name: "King's Indian", games: 67, wins: 55.2, avgMoves: 16.8 },
      { name: "Nimzo-Indian", games: 51, wins: 60.8, avgMoves: 15.2 },
    ],
  },
  // Most played variations
  variations: [
    { name: "Sicilian, Najdorf Variation", games: 78, wins: 64.1, avgMoves: 18.3 },
    { name: "Queen's Gambit Declined, Orthodox Defense", games: 65, wins: 70.8, avgMoves: 15.7 },
    { name: "Ruy Lopez, Closed Variation", games: 59, wins: 74.6, avgMoves: 17.2 },
    { name: "French Defense, Winawer Variation", games: 47, wins: 59.6, avgMoves: 16.4 },
    { name: "King's Indian, Classical Variation", games: 41, wins: 56.1, avgMoves: 18.9 },
  ],
  // Theory performance
  theoryPerformance: {
    inTheory: { games: 723, wins: 482, losses: 156, draws: 85 },
    outOfTheory: { games: 520, wins: 341, losses: 156, draws: 23 },
  },
  // Most common theory mistakes
  commonMistakes: [
    { move: "e4 e5 Nf3 Nc6 Bb5 a6 Ba4 Nf6 O-O Be7 Re1 b5 Bb3 d6 c3 O-O", mistake: "h3", correct: "d4", frequency: 12 },
    { move: "d4 Nf6 c4 e6 Nc3 Bb4", mistake: "Qc2", correct: "e3", frequency: 9 },
    { move: "e4 c5 Nf3 d6 d4 cxd4 Nxd4 Nf6 Nc3", mistake: "a6", correct: "e6", frequency: 8 },
    { move: "d4 d5 c4 e6 Nc3 Nf6 Bg5", mistake: "dxc4", correct: "Be7", frequency: 7 },
    { move: "e4 e5 Nf3 Nc6 Bc4", mistake: "Bc5", correct: "Nf6", frequency: 6 },
  ],
  // Theory study recommendations
  recommendations: [
    { opening: "Sicilian Defense, Dragon Variation", reason: "Weakness against this line", priority: "High" },
    { opening: "Queen's Gambit, Exchange Variation", reason: "Improve win rate", priority: "Medium" },
    { opening: "Ruy Lopez, Marshall Attack", reason: "Frequently encountered", priority: "High" },
    { opening: "King's Indian, Sämisch Variation", reason: "Theoretical advantage", priority: "Medium" },
    { opening: "French Defense, Tarrasch Variation", reason: "Improve understanding", priority: "Low" },
  ],
};

const UserProfile = (props: any) => {
  const [userData, setUserData] = useState(mockUserData);
  const [eloData, setEloData] = useState([]);
  const [formattedElo, setFormattedElo] = useState([{date: '', elo: [1000, 1000, 1000, 1000]}]);
  const [activityData, setActivityData] = useState(mockActivityData);
  const [gamesData, setGamesData] = useState(mockGamesData);
  const [theoryData, setTheoryData] = useState(mockTheoryData);
  const [accuracyData, setAccuracyData] = useState(mockAccuracyData);
  const [loading, setLoading] = useState(false);
  const [recentGames, setRecentGames] = useState([]);
  const [game_index, setGameIndex] = useState(0); // 0 - bullet, 1 - blitz, 2 - rapid, 3 - classical
  const [performance_data, setPerformanceData] = useState([]);
  
  // Animation states
  const [animationProgress, setAnimationProgress] = useState(0);
  const [animatedAccuracy, setAnimatedAccuracy] = useState(0);
  const [animatedOpeningAccuracy, setAnimatedOpeningAccuracy] = useState(0);
  const [animatedMiddlegameAccuracy, setAnimatedMiddlegameAccuracy] = useState(0);
  const [animatedEndgameAccuracy, setAnimatedEndgameAccuracy] = useState(0);
  const animationRef = useRef<NodeJS.Timeout | null>(null);
  
  // State for main profile tabs
  const [profileTab, setProfileTab] = useState(0);
  
  // State for filters and tabs
  const [timeFilter, setTimeFilter] = useState<'week' | 'month' | 'year' | 'all'>('month');
  const [gameMode, setGameMode] = useState<'bullet' | 'blitz' | 'rapid' | 'classical'>('blitz');
  const [gamesTab, setGamesTab] = useState(0);
  const [openingColor, setOpeningColor] = useState<'white' | 'black'>('white');

  function removeLastMove(movesString) {
  // Trim any leading or trailing whitespace to ensure accurate splitting.
  const trimmedString = movesString.trim();

  // If the string is empty after trimming, there are no moves to remove.
  if (trimmedString === "") {
    return "";
  }

  // Split the string into an array of individual moves using space as a delimiter.
  const movesArray = trimmedString.split(" ");

  // If there's only one move, return an empty string as removing it leaves nothing.
  if (movesArray.length === 1) {
    return "";
  }

  // Remove the last element from the array.
  movesArray.pop();

  // Join the remaining moves back into a string with spaces.
  return movesArray.join(" ");
}

  // Function to fetch user profile data from the server
  const fetchUserData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("chesscloud-token");
          if (token === null || token === undefined || typeof(token) !== 'string' || token.length < 10) return;
          handleTimeFilterChange('month', token);
          const response = await fetch(
            main_server + "play_profile",
            {
              method: "GET",
              headers: {
                "Content-Type": "text/plain",
                 Authorization: "Bearer " + token,
              },
            },
          );
          if (response.status === 200) {
              const data = await response.json();
              var total_wins = 0;
              var total_draws = 0;
              var total_losses = 0;
              for (const variation of data.stats.variations) {
                variation.games[0] === 1 ? total_wins++ : variation.games[1] === 1 ? total_draws++ : total_losses++;
              }
              const total_games = total_draws + total_losses + total_wins;
              setUserData({
  name: data.name,
  username: data.user,
  joinDate: data.date,
  totalGames: total_games,
  wins: total_wins,
  losses: total_losses,
  draws: total_draws,
  winRate: (total_wins / total_games) * 100,
  avatar: null, 
  currentAccuracy: 0, 
  elo: data.elo, 
  percentileRank: 0,
});
  setGamesData(data.games.map((val, index) => { return {id: index + 1,
    opponent: val.black,
    result: val.result === 0 ? 'win' : val.result === 1 ? 'draw' : 'loss',
    format: val.format,
    date: new Date(val.date).toLocaleDateString(),
    time: new Date(val.date).toLocaleDateString(),
    playerRating: val.eloW,
    opponentRating: val.eloB,
    ratingChange: 0
   }}));
   setPerformanceData(data.performance.map((val) => {return {wins: val.wins, draws: val.draws, losses: val.losses, gameType: val.format === 1 ? 'bullet' : val.format === 2 ? 'blitz' : val.format  === 3 ? 'rapid' : 'classical'}}));
   const avg_moves_w = [];
   const avg_moves_b = [];
   const openings_w = [];
   const openings_b = [];
   const variations = [];
   for (const variation of data.stats.variations) {
    // calculate avg moves in theory
    const totalGames = variation.games.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    const avgTheory = variation.theory / totalGames;
      (variation.white ? avg_moves_w : avg_moves_b).push(avgTheory);
    // unify variations into openings and count
      var opening_found = false;
      var variation_found = false;
      var opening_index = 0;
      for (const opening of (variation.white ? openings_w : openings_b)) {
        if (!opening_found) {
        if (opening.name.split(':')[0] === variation.name.split(':')[0]) {
          opening_found = true;
          (variation.white ? openings_w : openings_b)[opening_index].games += totalGames;
          (variation.white ? openings_w : openings_b)[opening_index].wins += variation.games[0];
          (variation.white ? openings_w : openings_b)[opening_index].avgTheory = ((variation.white ? openings_w : openings_b)[opening_index].avgTheory + avgTheory) / 2
        }
      }
        opening_index++;
      }
      if (!opening_found) (variation.white ? openings_w : openings_b).push({name: variation.name.split(':')[0], games: totalGames, wins: variation.games[0], avgMoves: avgTheory});
      for (const opening of variations) {
      if (!variation_found) {
        if (opening.name === variation.name) {
          variation_found = true;
          variations[opening_index].games += totalGames;
          variations[opening_index].wins += variation.games[0];
          variations[opening_index].avgTheory = ((variation.white ? openings_w : openings_b)[opening_index].avgTheory + avgTheory) / 2
        }
      }
    }
      if (!variation_found) variations.push({name: variation.name, games: totalGames, wins: variation.games[0], avgMoves: avgTheory});
    }
    const theory_data = 
    { 
    theoryMoves: {
    asWhite: avg_moves_w.reduce((accumulator, currentValue) => accumulator + currentValue, 0) / avg_moves_w.length,
    asBlack: avg_moves_b.reduce((accumulator, currentValue) => accumulator + currentValue, 0) / avg_moves_b.length,
  },
  // Opening repertoire
  openings: {
    white: openings_w,
    black: openings_b,
  },
  // Most played variations
  variations: variations,
  // Theory performance
  theoryPerformance: {
    inTheory: { games: 723, wins: 482, losses: 156, draws: 85 },
    outOfTheory: { games: 520, wins: 341, losses: 156, draws: 23 },
  },
  // Most common theory mistakes
  commonMistakes: data.stats.mistakes.map((mis) => { return {move: removeLastMove(mis.line), mistake: mis.line.split(' ')[mis.line.split(' ').length - 1], frequency: mis.count, correct: mis.best}}),
  // Theory study recommendations
  recommendations: [
    { opening: "Sicilian Defense, Dragon Variation", reason: "Weakness against this line", priority: "High" },
    { opening: "Queen's Gambit, Exchange Variation", reason: "Improve win rate", priority: "Medium" },
    { opening: "Ruy Lopez, Marshall Attack", reason: "Frequently encountered", priority: "High" },
    { opening: "King's Indian, Sämisch Variation", reason: "Theoretical advantage", priority: "Medium" },
    { opening: "French Defense, Tarrasch Variation", reason: "Improve understanding", priority: "Low" },
  ]};
   setTheoryData(theory_data);
          } else {
            // show alert !
          }
          setLoading(false);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);
  
  // No animation - set values directly
  useEffect(() => {
    if (profileTab === 2) {
      // Set all values directly to their final values
      setAnimationProgress(100);
      setAnimatedAccuracy(userData.currentAccuracy);
      setAnimatedOpeningAccuracy(accuracyData.gameStages.opening);
      setAnimatedMiddlegameAccuracy(accuracyData.gameStages.middlegame);
      setAnimatedEndgameAccuracy(accuracyData.gameStages.endgame);
    }
  }, [profileTab, userData.currentAccuracy, accuracyData.gameStages]);

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!userData.name) return "?";
    return userData.name
      .split(" ")
      .map(name => name[0])
      .join("")
      .toUpperCase();
  };

  // Handle time filter change
  const handleTimeFilterChange = async (filter: 'week' | 'month' | 'year' | 'all', token = props.token) => {
    try {
      const token = localStorage.getItem("chesscloud-token");
          if (token === null || token === undefined || typeof(token) !== 'string' || token.length < 10) return;
          const response = await fetch(
            `${main_server}elo_history?filter=${filter}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "text/plain",
                 Authorization: "Bearer " + token,
              },
            },
          );
          if (response.status === 200) {
              const data = await response.json();
              setEloData(data);
              console.log(data);
              setTimeFilter(filter);
          } else {

          }
        } catch (e) {

        }
  };

  // Handle game mode change
  const handleGameModeChange = (event: React.SyntheticEvent, newValue: 'bullet' | 'blitz' | 'rapid' | 'classical') => {
    setGameMode(newValue);
  };

  // Handle games tab change
  const handleGamesTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setGamesTab(newValue);
  };
  
  // Handle profile tab change
  const handleProfileTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setProfileTab(newValue);
  };
  
  // Handle opening color change
  const handleOpeningColorChange = (event: React.SyntheticEvent, newValue: 'white' | 'black') => {
    setOpeningColor(newValue);
  };

  // Get current ELO for selected game mode
  const getCurrentElo = () => {
    return eloData.length === 0 ? 0 : eloData[eloData.length - 1].elo[game_index];
  };

  // Calculate ELO change over the selected period
  const getEloChange = () => {
    return eloData.length === 0 ? 0 : eloData[eloData.length - 1].elo[game_index] - eloData[0].elo[game_index];
  };

  useEffect(() => {
    // const gameTypes = ['Bullet', 'Blitz', 'Rapid', 'Classical'];
    setRecentGames(gamesTab === 0 ? gamesData : gamesData.filter((val) => val.format == gamesTab));
  }, [gamesTab, gamesData]);

  useEffect(() => {
    setGameIndex(gameMode === 'bullet' ? 0 : gameMode === 'blitz' ? 1 : gameMode === 'rapid' ? 2 : 3);
    if (eloData.length > 0) {
      setFormattedElo(eloData.map((val) => { return {elo: val.elo[game_index], date: val.convertedDate}}));
    }
  }, [eloData, gameMode]);

  return (
    <div className={`userProfile`}>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress style={{ color: '#6ED0E8' }} />
        </div>
      ) : (
        <>
          {/* Profile Header */}
          <div className="profileHeader">
            <div className="profileAvatar">
              {userData.avatar ? (
                <img src={userData.avatar} alt={userData.name} />
              ) : (
                getUserInitials()
              )}
            </div>
            <div className="profileInfo">
              <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                <div>
                  <h1 className="profileName">{userData.name}</h1>
                  <p className="profileUsername">@{userData.username}</p>
                </div>
                
                {/* Simple ELO display with glass effect and glowing border 
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <div style={{ 
                    height: '40px',
                    background: 'rgba(30, 30, 45, 0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(110, 208, 232, 0.8)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 15px',
                    boxShadow: '0 0 10px rgba(110, 208, 232, 0.3)',
                    marginBottom: '4px'
                  }}>
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: 'white',
                    }}>
                      {userData.elo}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6ED0E8',
                    fontWeight: 'bold',
                  }}>
                    TOP 99.9%
                  </div>
                </div>*/}
              </div>
              
              <div style={{ 
                marginTop: '12px',
                marginBottom: '12px', 
                fontSize: '0.9rem', 
                color: '#6ED0E8',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span style={{ 
                  display: 'inline-block', 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  backgroundColor: '#6ED0E8' 
                }}></span>
                Higher rated than {userData.percentileRank}% of players
              </div>
              <div className="profileStats">
                <div className="statItem">
                  <div className="statValue">{userData.totalGames}</div>
                  <div className="statLabel">Games</div>
                </div>
                <div className="statItem">
                  <div className="statValue">{userData.wins}</div>
                  <div className="statLabel">Wins</div>
                </div>
                <div className="statItem">
                  <div className="statValue">{userData.losses}</div>
                  <div className="statLabel">Losses</div>
                </div>
                <div className="statItem">
                  <div className="statValue">{userData.draws}</div>
                  <div className="statLabel">Draws</div>
                </div>
                <div className="statItem">
                  <div className="statValue">{userData.winRate}%</div>
                  <div className="statLabel">Win Rate</div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Profile Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%', px: 2 }}>
            <StyledTabs
              value={profileTab}
              onChange={handleProfileTabChange}
              aria-label="profile tabs"
              centered
            >
              <StyledTab label="ELO & Performance" />
              <StyledTab label="Theory & Openings" />
              <StyledTab label="Accuracy" />
            </StyledTabs>
          </Box>

          {/* Profile Content */}
          <div className="profileContent">
            {/* ELO Tab Content */}
            {profileTab === 0 && (
              <>
                {/* ELO History Chart */}
                <div className="contentSection" style={{ gridColumn: '1 / -1' }}>
              <div className="sectionTitle">
                <div>
                  ELO History
                  <span style={{ marginLeft: '10px', color: '#6ED0E8', fontWeight: 'bold' }}>
                    {getCurrentElo()} 
                    <span 
                      style={{ 
                        marginLeft: '5px', 
                        color: getEloChange() > 0 ? '#4CAF50' : getEloChange() < 0 ? '#F44336' : '#9E9E9E'
                      }}
                    >
                      {getEloChange() > 0 ? `+${getEloChange()}` : getEloChange()}
                    </span>
                  </span>
                </div>
                <div className="timeFilter">
                  <button 
                    className={`timeFilterButton ${timeFilter === 'week' ? 'timeFilterButtonActive' : ''}`}
                    onClick={() => handleTimeFilterChange('week')}
                  >
                    Week
                  </button>
                  <button 
                    className={`timeFilterButton ${timeFilter === 'month' ? 'timeFilterButtonActive' : ''}`}
                    onClick={() => handleTimeFilterChange('month')}
                  >
                    Month
                  </button>
                  <button 
                    className={`timeFilterButton ${timeFilter === 'year' ? 'timeFilterButtonActive' : ''}`}
                    onClick={() => handleTimeFilterChange('year')}
                  >
                    Year
                  </button>
                  <button 
                    className={`timeFilterButton ${timeFilter === 'all' ? 'timeFilterButtonActive' : ''}`}
                    onClick={() => handleTimeFilterChange('all')}
                  >
                    All Time
                  </button>
                </div>
              </div>
              
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <StyledTabs
                  value={gameMode}
                  onChange={handleGameModeChange}
                  aria-label="game mode tabs"
                >
                  <StyledTab value="bullet" label="Bullet" />
                  <StyledTab value="blitz" label="Blitz" />
                  <StyledTab value="rapid" label="Rapid" />
                  <StyledTab value="classical" label="Classical" />
                </StyledTabs>
              </Box>
              
              <div className="eloChart">
                <LineChart
                  dataset={formattedElo}
                  xAxis={[
                    {
                      scaleType: "point",
                      dataKey: "date",
                      valueFormatter: (date) => {
                        // Format date based on time filter
                        const dateObj = new Date(date).toLocaleDateString();
                        return dateObj;
                      }
                    },
                  ]}
                  series={[
                    {
                      dataKey: "elo",
                      label: `${gameMode.charAt(0).toUpperCase() + gameMode.slice(1)} ELO`,
                      color: "#6ED0E8",
                      showMark: false,
                      curve: "natural",
                    },
                  ]}
                  height={400}
                  margin={{ top: 20, right: 20, bottom: 30, left: 40 }}
                  sx={{
                    "& .MuiChartsAxis-tickLabel": {
                      fill: props.dark ? "white" : "black",
                    },
                    "& .MuiChartsAxis-line": {
                      stroke: props.dark ? "white" : "black",
                    },
                    "& .MuiChartsAxis-tick": {
                      stroke: props.dark ? "white" : "black",
                    },
                  }}
                />
              </div>
                </div>

                {/* User Activity */}
                <div className="contentSection">
              <div className="sectionTitle">
                <div>Recent Activity</div>
              </div>
              <div className="activityFeed">
                {activityData.map((activity) => (
                  <div key={activity.id} className="activityItem">
                    <div className="activityIcon">{activity.icon}</div>
                    <div className="activityContent">
                      <div className="activityTitle">{activity.title}</div>
                      <div className="activityTime">{activity.time}</div>
                    </div>
                  </div>
                ))}
              </div>
                </div>

                {/* Games History */}
                <div className="contentSection">
              <div className="sectionTitle">
                <div>Recent Games</div>
              </div>
              
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <StyledTabs
                  value={gamesTab}
                  onChange={handleGamesTabChange}
                  aria-label="games tabs"
                >
                  <StyledTab label="All" />
                  <StyledTab label="Bullet" />
                  <StyledTab label="Blitz" />
                  <StyledTab label="Rapid" />
                  <StyledTab label="Classical" />
                </StyledTabs>
              </Box>
              
              <div className="gamesList">
                {recentGames.map((game) => (
                  <div key={game.id} className="gameItem">
                    <div className={`gameResult ${game.result === 'win' ? 'gameWin' : game.result === 'loss' ? 'gameLoss' : 'gameDraw'}`}>
                      {game.result === 'win' ? 'W' : game.result === 'loss' ? 'L' : 'D'}
                    </div>
                    <div className="gameInfo">
                      <div className="gameOpponent">vs {game.opponent}</div>
                      <div className="gameDetails">
                        <div className="gameType">{game.gameType}</div>
                        <div className="gameDate">{dayjs(game.date).format('MMM D, YYYY')} • {game.time}</div>
                        <div className="gameRating">
                          {game.playerRating}
                          <span className={`ratingChange ${game.ratingChange > 0 ? 'ratingUp' : game.ratingChange < 0 ? 'ratingDown' : 'ratingNeutral'}`}>
                            {game.ratingChange > 0 ? `+${game.ratingChange}` : game.ratingChange}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
                </div>

                {/* Performance Stats */}
                <div className="contentSection" style={{ gridColumn: '1 / -1' }}>
              <div className="sectionTitle">
                <div>Performance by Game Type</div>
              </div>
              <BarChart
                dataset={performance_data}
                xAxis={[{ scaleType: 'band', dataKey: 'gameType' }]}
                series={[
                  { dataKey: 'wins', label: 'Wins', color: '#4CAF50' },
                  { dataKey: 'losses', label: 'Losses', color: '#F44336' },
                  { dataKey: 'draws', label: 'Draws', color: '#9E9E9E' },
                ]}
                height={300}
                sx={{
                  "& .MuiChartsAxis-tickLabel": {
                    fill: props.dark ? "white" : "black",
                  },
                  "& .MuiChartsAxis-line": {
                    stroke: props.dark ? "white" : "black",
                  },
                  "& .MuiChartsAxis-tick": {
                    stroke: props.dark ? "white" : "black",
                  },
                }}
              />
                </div>
              </>
            )}
            
            {/* Accuracy Tab Content */}
            {profileTab === 2 && (
              <>
                {/* Accuracy Overview */}
                <div className="contentSection" style={{ gridColumn: '1 / -1' }}>
                  <div className="sectionTitle">
                    <div>Accuracy Overview</div>
                  </div>
                  
                  <Grid container spacing={3}>
                    {/* Current Accuracy Display */}
                    <Grid item xs={12} md={4}>
                      <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="h6" sx={{ mb: 2, color: '#888' }}>
                          Current Accuracy
                        </Typography>
                        <div style={{ position: 'relative', width: '180px', height: '180px', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto' }}>
                          <CircularProgress
                            variant="determinate"
                            value={100}
                            size={180}
                            thickness={4}
                            sx={{ color: 'rgba(110, 208, 232, 0.1)', position: 'absolute' }}
                          />
                          <CircularProgress
                            variant="determinate"
                            value={animatedAccuracy}
                            size={180}
                            thickness={4}
                            sx={{ 
                              color: userData.currentAccuracy > 90 ? '#4CAF50' : 
                                    userData.currentAccuracy > 80 ? '#8BC34A' : 
                                    userData.currentAccuracy > 70 ? '#FFC107' : 
                                    '#F44336',
                              position: 'absolute',
                              transition: 'all 0.2s ease-out'
                            }}
                          />
                          <div style={{ textAlign: 'center', padding: '0 15px' }}>
                            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#6ED0E8', fontSize: { xs: '2rem', sm: '2.5rem' } }}>
                              {Math.round(animatedAccuracy * 10) / 10}%
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#888', fontSize: '0.8rem', mt: 1 }}>
                              {userData.currentAccuracy > 90 ? 'Excellent' : 
                               userData.currentAccuracy > 80 ? 'Very Good' : 
                               userData.currentAccuracy > 70 ? 'Good' : 
                               'Needs Improvement'}
                            </Typography>
                          </div>
                        </div>
                        <Typography variant="body2" sx={{ mt: 2, color: '#888', textAlign: 'center' }}>
                          Your accuracy is higher than 92% of players at your level
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    {/* Game Stage Accuracy */}
                    <Grid item xs={12} md={8}>
                      <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px', height: '100%' }}>
                        <Typography variant="h6" sx={{ mb: 3, color: '#888' }}>
                          Accuracy by Game Stage
                        </Typography>
                        <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', height: '180px' }}>
                          {/* Opening */}
                          <div style={{ textAlign: 'center', width: '30%' }}>
                            <div style={{ position: 'relative', width: '120px', height: '120px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                              <CircularProgress
                                variant="determinate"
                                value={100}
                                size={120}
                                thickness={4}
                                sx={{ color: 'rgba(110, 208, 232, 0.1)', position: 'absolute' }}
                              />
                              <CircularProgress
                                variant="determinate"
                                value={animatedOpeningAccuracy}
                                size={120}
                                thickness={4}
                                sx={{ color: '#4CAF50', position: 'absolute', transition: 'all 0.2s ease-out' }}
                              />
                              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                {Math.round(animatedOpeningAccuracy * 10) / 10}%
                              </Typography>
                            </div>
                            <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold' }}>
                              Opening
                            </Typography>
                          </div>
                          
                          {/* Middlegame */}
                          <div style={{ textAlign: 'center', width: '30%' }}>
                            <div style={{ position: 'relative', width: '120px', height: '120px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                              <CircularProgress
                                variant="determinate"
                                value={100}
                                size={120}
                                thickness={4}
                                sx={{ color: 'rgba(110, 208, 232, 0.1)', position: 'absolute' }}
                              />
                              <CircularProgress
                                variant="determinate"
                                value={animatedMiddlegameAccuracy}
                                size={120}
                                thickness={4}
                                sx={{ color: '#8BC34A', position: 'absolute', transition: 'all 0.2s ease-out' }}
                              />
                              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                {Math.round(animatedMiddlegameAccuracy * 10) / 10}%
                              </Typography>
                            </div>
                            <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold' }}>
                              Middlegame
                            </Typography>
                          </div>
                          
                          {/* Endgame */}
                          <div style={{ textAlign: 'center', width: '30%' }}>
                            <div style={{ position: 'relative', width: '120px', height: '120px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                              <CircularProgress
                                variant="determinate"
                                value={100}
                                size={120}
                                thickness={4}
                                sx={{ color: 'rgba(110, 208, 232, 0.1)', position: 'absolute' }}
                              />
                              <CircularProgress
                                variant="determinate"
                                value={animatedEndgameAccuracy}
                                size={120}
                                thickness={4}
                                sx={{ color: '#FFC107', position: 'absolute', transition: 'all 0.2s ease-out' }}
                              />
                              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                {Math.round(animatedEndgameAccuracy * 10) / 10}%
                              </Typography>
                            </div>
                            <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold' }}>
                              Endgame
                            </Typography>
                          </div>
                        </div>
                      </Paper>
                    </Grid>
                  </Grid>
                </div>
                
                {/* Accuracy History Chart */}
                <div className="contentSection" style={{ gridColumn: '1 / -1' }}>
                  <div className="sectionTitle">
                    <div>
                      Accuracy Trend
                      <span style={{ marginLeft: '10px', color: '#6ED0E8', fontWeight: 'bold' }}>
                        {accuracyData.historyData[accuracyData.historyData.length - 1].accuracy}% 
                        <span 
                          style={{ 
                            marginLeft: '5px', 
                            color: (accuracyData.historyData[accuracyData.historyData.length - 1].accuracy - accuracyData.historyData[0].accuracy) > 0 ? '#4CAF50' : 
                                   (accuracyData.historyData[accuracyData.historyData.length - 1].accuracy - accuracyData.historyData[0].accuracy) < 0 ? '#F44336' : '#9E9E9E'
                          }}
                        >
                          {(accuracyData.historyData[accuracyData.historyData.length - 1].accuracy - accuracyData.historyData[0].accuracy) > 0 ? 
                            `+${(accuracyData.historyData[accuracyData.historyData.length - 1].accuracy - accuracyData.historyData[0].accuracy).toFixed(1)}` : 
                            (accuracyData.historyData[accuracyData.historyData.length - 1].accuracy - accuracyData.historyData[0].accuracy).toFixed(1)}%
                        </span>
                      </span>
                    </div>
                    <div className="timeFilter">
                      <button 
                        className={`timeFilterButton ${timeFilter === 'week' ? 'timeFilterButtonActive' : ''}`}
                        onClick={() => handleTimeFilterChange('week')}
                      >
                        Week
                      </button>
                      <button 
                        className={`timeFilterButton ${timeFilter === 'month' ? 'timeFilterButtonActive' : ''}`}
                        onClick={() => handleTimeFilterChange('month')}
                      >
                        Month
                      </button>
                      <button 
                        className={`timeFilterButton ${timeFilter === 'year' ? 'timeFilterButtonActive' : ''}`}
                        onClick={() => handleTimeFilterChange('year')}
                      >
                        Year
                      </button>
                      <button 
                        className={`timeFilterButton ${timeFilter === 'all' ? 'timeFilterButtonActive' : ''}`}
                        onClick={() => handleTimeFilterChange('all')}
                      >
                        All Time
                      </button>
                    </div>
                  </div>
                  
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <StyledTabs
                      value={gameMode}
                      onChange={handleGameModeChange}
                      aria-label="game mode tabs"
                    >
                      <StyledTab value="bullet" label="Bullet" />
                      <StyledTab value="blitz" label="Blitz" />
                      <StyledTab value="rapid" label="Rapid" />
                      <StyledTab value="classical" label="Classical" />
                    </StyledTabs>
                  </Box>
                  
                  <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px' }}>
                    <LineChart
                      dataset={accuracyData.historyData.map((item) => ({
                        date: item.date,
                        accuracy: item.accuracy,
                      }))}
                      xAxis={[
                        {
                          scaleType: "point",
                          dataKey: "date",
                          valueFormatter: (date) => {
                            // Format date based on time filter
                            const dateObj = dayjs(date);
                            if (timeFilter === 'week') return dateObj.format('ddd');
                            if (timeFilter === 'month') return dateObj.format('MMM D');
                            if (timeFilter === 'year') return dateObj.format('MMM');
                            return dateObj.format('MMM YYYY');
                          }
                        },
                      ]}
                      series={[
                        {
                          dataKey: "accuracy",
                          label: `${gameMode.charAt(0).toUpperCase() + gameMode.slice(1)} Accuracy`,
                          color: "#6ED0E8",
                          showMark: true,
                          curve: "natural",
                          valueFormatter: (value) => `${value}%`,
                        },
                      ]}
                      height={300}
                      margin={{ top: 20, right: 20, bottom: 30, left: 40 }}
                      sx={{
                        "& .MuiChartsAxis-tickLabel": {
                          fill: props.dark ? "white" : "black",
                        },
                        "& .MuiChartsAxis-line": {
                          stroke: props.dark ? "white" : "black",
                        },
                        "& .MuiChartsAxis-tick": {
                          stroke: props.dark ? "white" : "black",
                        },
                      }}
                    />
                  </Paper>
                </div>
                
                {/* Accuracy by Time Control */}
                <div className="contentSection">
                  <div className="sectionTitle">
                    <div>Accuracy by Time Control</div>
                  </div>
                  
                  <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px' }}>
                    <BarChart
                      dataset={[
                        { timeControl: 'Bullet', accuracy: accuracyData.timeControls.bullet },
                        { timeControl: 'Blitz', accuracy: accuracyData.timeControls.blitz },
                        { timeControl: 'Rapid', accuracy: accuracyData.timeControls.rapid },
                        { timeControl: 'Classical', accuracy: accuracyData.timeControls.classical },
                      ]}
                      xAxis={[{ scaleType: 'band', dataKey: 'timeControl' }]}
                      series={[
                        { 
                          dataKey: 'accuracy', 
                          label: 'Accuracy %',
                          valueFormatter: (value) => `${value}%`,
                          color: '#6ED0E8'
                        },
                      ]}
                      height={250}
                      sx={{
                        "& .MuiChartsAxis-tickLabel": {
                          fill: props.dark ? "white" : "black",
                        },
                        "& .MuiChartsAxis-line": {
                          stroke: props.dark ? "white" : "black",
                        },
                        "& .MuiChartsAxis-tick": {
                          stroke: props.dark ? "white" : "black",
                        },
                      }}
                    />
                  </Paper>
                </div>
                
                {/* Piece Movement Accuracy */}
                <div className="contentSection">
                  <div className="sectionTitle">
                    <div>Piece Movement Accuracy</div>
                  </div>
                  
                  <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', gap: '10px' }}>
                      {Object.entries(accuracyData.pieceAccuracy).map(([piece, accuracy]) => (
                        <div key={piece} style={{ textAlign: 'center', width: '120px', margin: '10px' }}>
                          <div style={{ 
                            width: '60px', 
                            height: '60px', 
                            borderRadius: '50%', 
                            backgroundColor: 'rgba(110, 208, 232, 0.1)', 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            margin: '0 auto',
                            position: 'relative',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              width: '100%',
                              height: `${Number(accuracy)}%`,
                              backgroundColor: '#6ED0E8',
                              opacity: 0.7
                            }}></div>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', position: 'relative', zIndex: 1 }}>
                              {piece.charAt(0).toUpperCase()}
                            </Typography>
                          </div>
                          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                            {piece.charAt(0).toUpperCase() + piece.slice(1)}
                          </Typography>
                            <Typography variant="body2" sx={{ color: '#6ED0E8', fontWeight: 'bold' }}>
                              {Math.round(Number(accuracy) * 10) / 10}%
                            </Typography>
                        </div>
                      ))}
                    </div>
                  </Paper>
                </div>
                
                {/* Recent Games Accuracy */}
                <div className="contentSection" style={{ gridColumn: '1 / -1' }}>
                  <div className="sectionTitle">
                    <div>Recent Games Accuracy</div>
                  </div>
                  
                  <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px' }}>
                    <div className="gamesList">
                      {accuracyData.recentGames.map((game) => (
                        <div key={game.id} className="gameItem">
                          <div className={`gameResult ${game.result === 'win' ? 'gameWin' : game.result === 'loss' ? 'gameLoss' : 'gameDraw'}`}>
                            {game.result === 'win' ? 'W' : game.result === 'loss' ? 'L' : 'D'}
                          </div>
                          <div className="gameInfo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <div>
                              <div className="gameOpponent">vs {game.opponent}</div>
                              <div className="gameDetails">
                                <div className="gameType">Your Accuracy: {game.accuracy}%</div>
                                <div className="gameDate">Opponent Accuracy: {game.opponentAccuracy}%</div>
                              </div>
                            </div>
                            <div className="gameRating">
                              <div style={{ 
                                backgroundColor: game.accuracy > game.opponentAccuracy ? '#4CAF50' : 
                                                game.accuracy < game.opponentAccuracy ? '#F44336' : '#FFC107',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontWeight: 'bold'
                              }}>
                                {game.accuracy > game.opponentAccuracy ? '+' : game.accuracy < game.opponentAccuracy ? '-' : '='}{Math.abs(game.accuracy - game.opponentAccuracy).toFixed(1)}%
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Paper>
                </div>
                
                {/* Improvement Areas */}
                <div className="contentSection" style={{ gridColumn: '1 / -1' }}>
                  <div className="sectionTitle">
                    <div>Improvement Areas</div>
                  </div>
                  
                  <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                      {accuracyData.improvementAreas.map((area, index) => (
                        <div key={index} style={{ 
                          flex: '1 1 calc(33.333% - 20px)', 
                          minWidth: '250px',
                          padding: '15px',
                          backgroundColor: 'rgba(110, 208, 232, 0.05)',
                          borderRadius: '8px',
                          border: '1px solid rgba(110, 208, 232, 0.1)'
                        }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
                            {area.area}
                          </Typography>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                            <div style={{ flex: 1, height: '8px', backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', overflow: 'hidden' }}>
                              <div style={{ 
                                width: `${area.currentAccuracy}%`, 
                                height: '100%', 
                                backgroundColor: '#6ED0E8',
                                borderRadius: '4px',
                                transition: 'none'
                              }}></div>
                            </div>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#6ED0E8' }}>
                              {Math.round(area.currentAccuracy * 10) / 10}%
                            </Typography>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: '#888' }}>
                              Current Accuracy
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                              +{area.potentialGain}% potential gain
                            </Typography>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Paper>
                </div>
              </>
            )}
            
            {/* Theory Tab Content */}
            {profileTab === 1 && (
              <>
                {/* Theory Overview */}
                <div className="contentSection" style={{ gridColumn: '1 / -1' }}>
                  <div className="sectionTitle">
                    <div>Theory Overview</div>
                  </div>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={6} md={5}>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px' }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                          Average Moves in Theory
                        </Typography>
                        <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', marginBottom: '20px' }}>
                          <div style={{ textAlign: 'center' }}>
                            <Typography variant="h3" sx={{ color: '#6ED0E8', fontWeight: 'bold' }}>
                              {theoryData.theoryMoves.asWhite}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#888' }}>
                              As White
                            </Typography>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <Typography variant="h3" sx={{ color: '#6ED0E8', fontWeight: 'bold' }}>
                              {theoryData.theoryMoves.asBlack}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#888' }}>
                              As Black
                            </Typography>
                          </div>
                        </div>
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12} md={7}>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px', width: '100%' }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                          Theory Performance
                        </Typography>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <PieChart
                            series={[
                              {
                                data: [
                                  { id: 0, value: theoryData.theoryPerformance.inTheory.wins, label: '', color: '#4CAF50' },
                                  { id: 1, value: theoryData.theoryPerformance.inTheory.losses, label: '', color: '#F44336' },
                                  { id: 2, value: theoryData.theoryPerformance.outOfTheory.wins, label: '', color: '#8BC34A' },
                                  { id: 3, value: theoryData.theoryPerformance.outOfTheory.losses, label: '', color: '#FF5722' },
                                ],
                                highlightScope: { faded: 'global', highlighted: 'item' },
                                faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                              },
                            ]}
                            height={180}
                            width={300}
                            margin={{ right: 5, left: 5, top: 5, bottom: 5 }}
                            slotProps={{
                              legend: { hidden: true }
                            }}
                          />
                          
                          {/* Custom legend */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px', marginTop: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <div style={{ width: '12px', height: '12px', backgroundColor: '#4CAF50', marginRight: '5px', borderRadius: '2px' }}></div>
                              <Typography variant="caption">In Theory Wins</Typography>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <div style={{ width: '12px', height: '12px', backgroundColor: '#F44336', marginRight: '5px', borderRadius: '2px' }}></div>
                              <Typography variant="caption">In Theory Losses</Typography>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <div style={{ width: '12px', height: '12px', backgroundColor: '#8BC34A', marginRight: '5px', borderRadius: '2px' }}></div>
                              <Typography variant="caption">Out of Theory Wins</Typography>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <div style={{ width: '12px', height: '12px', backgroundColor: '#FF5722', marginRight: '5px', borderRadius: '2px' }}></div>
                              <Typography variant="caption">Out of Theory Losses</Typography>
                            </div>
                          </div>
                        </div>
                      </Paper>
                    </Grid>
                  </Grid>
                </div>
                
                {/* Opening Repertoire */}
                <div className="contentSection" style={{ gridColumn: '1 / -1' }}>
                  <div className="sectionTitle">
                    <div>Opening Repertoire</div>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <StyledTabs
                        value={openingColor}
                        onChange={handleOpeningColorChange}
                        aria-label="opening color tabs"
                      >
                        <StyledTab value="white" label="As White" />
                        <StyledTab value="black" label="As Black" />
                      </StyledTabs>
                    </Box>
                  </div>
                  
                  <div className="openingsList">
                    {theoryData.openings[openingColor].map((opening, index) => (
                      <div key={index} className="gameItem">
                        <div className="gameInfo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <div>
                            <div className="gameOpponent">{opening.name}</div>
                            <div className="gameDetails">
                              <div className="gameType">{opening.games} games</div>
                              <div className="gameDate">Avg. {opening.avgMoves} moves in theory</div>
                            </div>
                          </div>
                          <div className="gameRating">
                            <div style={{ 
                              backgroundColor: opening.wins >= 65 ? '#4CAF50' : opening.wins >= 50 ? '#FFC107' : '#F44336',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontWeight: 'bold'
                            }}>
                              {(opening.wins / opening.games) * 100}% Win Rate
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Most Common Variations */}
                <div className="contentSection">
                  <div className="sectionTitle">
                    <div>Most Played Variations</div>
                  </div>
                  
                  <div className="variationsList">
                    {theoryData.variations.map((variation, index) => (
                      <div key={index} className="gameItem">
                        <div className="gameInfo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <div>
                            <div className="gameOpponent">{variation.name}</div>
                            <div className="gameDetails">
                              <div className="gameType">{variation.games} games</div>
                              <div className="gameDate">Avg. {variation.avgMoves} moves in theory</div>
                            </div>
                          </div>
                          <div className="gameRating">
                            <div style={{ 
                              backgroundColor: variation.wins >= 65 ? '#4CAF50' : variation.wins >= 50 ? '#FFC107' : '#F44336',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontWeight: 'bold'
                            }}>
                              {(variation.wins / variation.games) * 100}% Win Rate
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Theory Recommendations */}
                <div className="contentSection">
                  <div className="sectionTitle">
                    <div>Study Recommendations</div>
                  </div>
                  
                  <div className="recommendationsList">
                    {theoryData.recommendations.map((rec, index) => (
                      <div key={index} className="activityItem">
                        <div className="activityIcon" style={{ 
                          backgroundColor: rec.priority === 'High' ? 'rgba(244, 67, 54, 0.2)' : 
                                          rec.priority === 'Medium' ? 'rgba(255, 193, 7, 0.2)' : 
                                          'rgba(76, 175, 80, 0.2)',
                          color: rec.priority === 'High' ? '#F44336' : 
                                rec.priority === 'Medium' ? '#FFC107' : 
                                '#4CAF50'
                        }}>
                          {rec.priority === 'High' ? '!' : rec.priority === 'Medium' ? '•' : '✓'}
                        </div>
                        <div className="activityContent">
                          <div className="activityTitle">{rec.opening}</div>
                          <div className="activityTime">{rec.reason} • {rec.priority} Priority</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Common Theory Mistakes */}
                <div className="contentSection" style={{ gridColumn: '1 / -1' }}>
                  <div className="sectionTitle">
                    <div>Common Theory Mistakes</div>
                  </div>
                  
                  <div className="mistakesList">
                    {theoryData.commonMistakes.map((mistake, index) => (
                      <Paper key={index} elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#888' }}>
                          Position after: <span style={{ fontFamily: 'monospace' }}>{mistake.move}</span>
                        </Typography>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div>
                            <Typography variant="body2" sx={{ color: '#F44336' }}>
                              Your move: <span style={{ fontWeight: 'bold' }}>{mistake.mistake}</span>
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                              Better move: <span style={{ fontWeight: 'bold' }}>{mistake.correct}</span>
                            </Typography>
                          </div>
                          <Typography variant="body2" sx={{ color: '#888' }}>
                            Made {mistake.frequency} times
                          </Typography>
                        </div>
                      </Paper>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UserProfile;
